import React, { useEffect, useState } from "react";
import { api, fmtEUR } from "../../lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [tab, setTab] = useState("applications");
  const [analytics, setAnalytics] = useState(null);
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody] = useState("");

  const load = async () => {
    const [a, ap, u, ath] = await Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/applications"),
      api.get("/admin/users"),
      api.get("/athletes"),
    ]);
    setAnalytics(a.data);
    setApps(ap.data);
    setUsers(u.data);
    setAthletes(ath.data);
  };
  useEffect(() => { load(); }, []);

  const decide = async (app_id, decision) => {
    try {
      await api.post(`/admin/applications/${app_id}/decision`, { decision });
      toast.success(`Application ${decision}d`);
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleFeature = async (athlete_id, field, value) => {
    try {
      await api.patch(`/admin/athletes/${athlete_id}`, { [field]: value });
      load();
    } catch { toast.error("Failed"); }
  };

  const postAnnouncement = async () => {
    if (!annTitle || !annBody) return;
    try {
      await api.post("/admin/announcements", { title: annTitle, body: annBody });
      toast.success("Announcement posted");
      setAnnTitle(""); setAnnBody("");
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-dashboard">
      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-border mb-10" data-testid="admin-analytics">
          {[
            ["Total golfers", analytics.total_athletes],
            ["Pending apps", analytics.pending_applications],
            ["Total donated", fmtEUR(analytics.total_donated)],
            ["Sponsorship value", fmtEUR(analytics.total_sponsorship)],
          ].map(([k, v], i) => (
            <div key={k} className={`p-6 ${i % 4 !== 3 ? "md:border-r border-border" : ""} ${i < 2 ? "border-b md:border-b-0 border-border" : ""}`}>
              <div className="overline text-foreground/60">{k}</div>
              <div className="font-display text-3xl mt-2">{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border" data-testid="admin-tabs">
        {[
          ["applications", "Applications"],
          ["golfers", "Golfers"],
          ["announcements", "Announcements"],
          ["users", "Users"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-5 py-3 text-sm ${tab === k ? "border-b-2 border-primary text-foreground" : "text-foreground/60"}`}
            data-testid={`admin-tab-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "applications" && (
          <div className="border border-border" data-testid="admin-applications">
            {apps.length === 0 && <div className="p-8 text-foreground/60">No applications yet.</div>}
            {apps.map((a) => (
              <div key={a.application_id} className="p-6 border-b border-border last:border-b-0 grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-6">
                  <div className="overline">
                    {[a.country, a.tour_level, a.handicap != null ? `HCP ${a.handicap}` : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  <div className="font-display text-2xl mt-1">{a.name}</div>
                  <div className="text-sm text-foreground/60 mt-1">{a.email} · Goal {fmtEUR(a.funding_goal)} · Club: {a.golf_club}</div>
                  <p className="text-sm mt-3 text-foreground/80 line-clamp-3">{a.story}</p>
                </div>
                <div className="md:col-span-3 text-sm">
                  <div className="overline">Status</div>
                  <div className="mt-1 font-medium capitalize">{a.status}</div>
                </div>
                <div className="md:col-span-3 flex md:flex-col gap-2">
                  {a.status === "pending" && (
                    <>
                      <button onClick={() => decide(a.application_id, "approve")} className="btn-primary text-xs" data-testid={`approve-${a.application_id}`}>Approve</button>
                      <button onClick={() => decide(a.application_id, "reject")} className="btn-outline text-xs" data-testid={`reject-${a.application_id}`}>Reject</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "golfers" && (
          <div className="border border-border" data-testid="admin-athletes">
            {athletes.map((a) => (
              <div key={a.athlete_id} className="p-5 border-b border-border last:border-b-0 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                <div className="md:col-span-6 flex items-center gap-4">
                  <img src={a.image_url} alt="" className="w-14 h-14 object-cover" />
                  <div>
                    <div className="font-display text-xl">{a.name}</div>
                    <div className="text-xs text-foreground/60">{[a.country, a.tour_level].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
                <div className="md:col-span-2 text-sm">{fmtEUR(a.funding_raised)} / {fmtEUR(a.funding_goal)}</div>
                <div className="md:col-span-4 flex flex-wrap gap-2 justify-end">
                  <button onClick={() => toggleFeature(a.athlete_id, "featured", !a.featured)} className={`text-xs px-3 py-1 border ${a.featured ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`toggle-featured-${a.athlete_id}`}>
                    {a.featured ? "Featured ✓" : "Feature"}
                  </button>
                  <button onClick={() => toggleFeature(a.athlete_id, "rising_star", !a.rising_star)} className={`text-xs px-3 py-1 border ${a.rising_star ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid={`toggle-rising-${a.athlete_id}`}>
                    {a.rising_star ? "Rising ✓" : "Mark rising"}
                  </button>
                  <button onClick={() => toggleFeature(a.athlete_id, "tgi_featured", !a.tgi_featured)} className={`text-xs px-3 py-1 border ${a.tgi_featured ? "bg-[hsl(var(--secondary))] border-[hsl(var(--secondary))]" : "border-border"}`} data-testid={`toggle-tgi-${a.athlete_id}`}>
                    {a.tgi_featured ? "In TGI ✓" : "Mark TGI"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "announcements" && (
          <div data-testid="admin-announcements">
            <div className="border border-border p-6 mb-8">
              <div className="overline mb-4">New announcement</div>
              <input value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} placeholder="Title" className="w-full h-12 px-4 border border-border bg-background outline-none focus:border-primary mb-3" data-testid="ann-title" />
              <textarea value={annBody} onChange={(e) => setAnnBody(e.target.value)} rows={4} placeholder="Body" className="w-full p-3 border border-border bg-background outline-none focus:border-primary mb-3" data-testid="ann-body" />
              <button onClick={postAnnouncement} className="btn-primary" data-testid="ann-post-btn">Post announcement</button>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="border border-border" data-testid="admin-users">
            {users.map((u) => (
              <div key={u.user_id} className="p-5 border-b border-border last:border-b-0 grid grid-cols-12 gap-4 items-center">
                <div className="col-span-6">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-foreground/60">{u.email}</div>
                </div>
                <div className="col-span-3 text-sm capitalize">{u.role}</div>
                <div className="col-span-3 text-xs text-foreground/50">{new Date(u.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
